import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";

/**
 * PhotoTour — slideshow "Ken Burns" cinematico su foto già caricate.
 * Fullscreen fisso quando `fullscreen=true`, altrimenti embedded con aspect 16:9.
 * Alterna 4 direzioni di zoom/pan (`kb-*`) per un effetto tour dinamico.
 */
export default function PhotoTour({ photos, title, onClose, fullscreen = false, autoAdvanceMs = 4200 }) {
    const total = photos?.length || 0;
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);

    const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
    const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);

    useEffect(() => {
        if (paused || total < 2) return;
        const t = setTimeout(next, autoAdvanceMs);
        return () => clearTimeout(t);
    }, [idx, paused, total, autoAdvanceMs, next]);

    useEffect(() => {
        if (!fullscreen) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
            if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [fullscreen, next, prev, onClose]);

    if (!total) return null;

    const kbClass = ["kb-tl", "kb-br", "kb-tr", "kb-bl"][idx % 4];

    const wrapCls = fullscreen
        ? "fixed inset-0 z-[90] bg-black flex flex-col"
        : "relative w-full aspect-video bg-black overflow-hidden rounded-sm border border-white/10";

    return (
        <div className={wrapCls} data-testid={fullscreen ? "photo-tour-fullscreen" : "photo-tour-preview"}>
            {fullscreen && (
                <div className="flex items-center justify-between px-4 md:px-6 py-3 bg-ink-950 border-b border-white/10 shrink-0">
                    <div className="flex items-center gap-2 text-ink-50">
                        <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                        <span className="font-serif text-sm md:text-base truncate">Tour dell&apos;immobile · {title}</span>
                    </div>
                    <button
                        data-testid="photo-tour-close"
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center gap-1.5 text-ink-100 hover:text-gold transition-colors text-sm"
                    >
                        <X size={18} /> Chiudi
                    </button>
                </div>
            )}
            <div className="relative flex-1 w-full overflow-hidden">
                {photos.map((src, i) => (
                    <div
                        key={i}
                        className={`absolute inset-0 transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`}
                        aria-hidden={i !== idx}
                    >
                        <img
                            src={src}
                            alt=""
                            className={`w-full h-full object-cover ${i === idx && !paused ? kbClass : ""}`}
                            style={{ transformOrigin: "center" }}
                        />
                        {/* Vignette */}
                        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0) 25%, rgba(0,0,0,0) 65%, rgba(0,0,0,0.55) 100%)" }} />
                    </div>
                ))}

                {/* Controls */}
                <div className="absolute inset-0 flex items-center justify-between px-2 md:px-4 pointer-events-none">
                    <button
                        data-testid="photo-tour-prev"
                        type="button"
                        onClick={prev}
                        aria-label="Foto precedente"
                        className="pointer-events-auto w-11 h-11 md:w-12 md:h-12 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur transition-colors"
                    >
                        <ChevronLeft size={22} />
                    </button>
                    <button
                        data-testid="photo-tour-next"
                        type="button"
                        onClick={next}
                        aria-label="Foto successiva"
                        className="pointer-events-auto w-11 h-11 md:w-12 md:h-12 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur transition-colors"
                    >
                        <ChevronRight size={22} />
                    </button>
                </div>

                {/* Bottom bar */}
                <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 py-3 flex items-center gap-3 bg-gradient-to-t from-black/70 to-transparent">
                    <button
                        data-testid="photo-tour-toggle-play"
                        type="button"
                        onClick={() => setPaused((p) => !p)}
                        aria-label={paused ? "Riprendi" : "Pausa"}
                        className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur transition-colors"
                    >
                        {paused ? <Play size={15} fill="currentColor" className="ml-0.5" /> : <Pause size={15} />}
                    </button>
                    <div className="flex-1 h-[3px] bg-white/20 rounded-full overflow-hidden">
                        <div className="h-full bg-gold transition-all duration-300" style={{ width: `${((idx + 1) / total) * 100}%` }} />
                    </div>
                    <span className="text-[11px] tracking-widest uppercase text-white/80 font-medium">{idx + 1} / {total}</span>
                </div>
            </div>
        </div>
    );
}
